/**
 * Feature-flag + trading-type type definitions.
 *
 * A "feature flag" is a single named boolean. A "trading type" is a trading
 * vertical (perpetual futures, spot, options, prediction markets) that is
 * *gated by* a feature flag of the same key and additionally carries the
 * metadata the app needs to route to it, label it and map it onto the backend
 * instrument type.
 *
 * Adding a vertical = adding one `FeatureFlagKey` member + one `TRADING_TYPES`
 * row + one env line. TypeScript enforces the last two (see `registry.ts` and
 * `env.ts`), so a half-added vertical does not compile.
 */
import type { InstrumentTypeResponse } from "../../codegen-api";

/**
 * Every flag the app knows about. The member name is also the suffix of its
 * environment variable: `PERPETUAL_SPOT` -> `REACT_APP_FEATURE_PERPETUAL_SPOT`.
 */
export enum FeatureFlagKey {
  // Trading verticals.
  PERPETUAL_FUTURES = "PERPETUAL_FUTURES",
  PERPETUAL_SPOT = "PERPETUAL_SPOT",
  OPTIONS = "OPTIONS",
  PREDICTION_MARKET = "PREDICTION_MARKET",

  // UI surfaces that are built but not necessarily ready for users.
  NATIVE_CANDLE_CHART = "NATIVE_CANDLE_CHART",
  ADVANCED_CHART_UI = "ADVANCED_CHART_UI",
  PWA_UI = "PWA_UI",
  TRANSFER_UI = "TRANSFER_UI",
  CONVERT_UI = "CONVERT_UI",
  USER_SETTINGS_UI = "USER_SETTINGS_UI",

  // UI that is off because using it would not work end to end. Each entry says
  // WHY, because the reasons differ and one of them is not "unsupported".
  //
  // Judge these against perpetual-backend's own routes and controllers, NOT
  // against src/codegen-api: that client is generated from the Aevo-compatible
  // OpenAPI spec, so an endpoint appearing there proves the spec has it, not
  // that we implement it. A review read the client and concluded three of these
  // flags were unjustified; checking the backend showed the opposite.
  ISOLATED_MARGIN_ADJUST = "ISOLATED_MARGIN_ADJUST",
  EDIT_ORDER = "EDIT_ORDER",
  API_KEYS_UI = "API_KEYS_UI",
  REFERRAL_REWARDS = "REFERRAL_REWARDS",
  SUBACCOUNTS_UI = "SUBACCOUNTS_UI",
}

/** The subset of `FeatureFlagKey` that gates a trading vertical. */
export type TradingTypeKey =
  | FeatureFlagKey.PERPETUAL_FUTURES
  | FeatureFlagKey.PERPETUAL_SPOT
  | FeatureFlagKey.OPTIONS
  | FeatureFlagKey.PREDICTION_MARKET;

export interface FeatureFlagDefinition {
  key: FeatureFlagKey;
  /** Human description, surfaced in `.env.example` and docs. */
  description: string;
  /**
   * Value used when no chain override, env override or dev override applies.
   * This is the ONLY implicit value in the system — everything else is either
   * explicitly configured or an error.
   */
  defaultEnabled: boolean;
}

export interface TradingTypeDefinition {
  /** Flag that gates this vertical. Also its stable identity. */
  key: TradingTypeKey;
  /** URL segment, e.g. `/perpetual/eth` -> "perpetual". Must be lowercase. */
  routeSegment: string;
  /**
   * Backend instrument type this vertical trades, or `null` when the vertical
   * has no API instrument type yet (prediction markets). A `null` vertical can
   * never become the selected market.
   */
  instrumentType: InstrumentTypeResponse | null;
  /** i18n key under the `app` namespace's `tradingTypes` block. */
  labelKey: string;
  /**
   * Compact i18n key for tight surfaces (the portfolio filter chip), where the
   * full name would wrap. Same block as `labelKey`.
   */
  shortLabelKey: string;
  /**
   * Suffix appended to the asset in the market-selection button title
   * (e.g. "-USD" for perps, "-USDC" for spot). `null` uses the full asset name.
   */
  quoteLabelSuffix: string | null;
  /** Sort order for nav, market-picker sections and default-vertical selection. */
  order: number;
  /**
   * True when a trading UI for this vertical exists in the app today. Enabling
   * a flag whose vertical is not implemented is a configuration error and
   * throws at boot (see `validate.ts`) rather than rendering an empty page.
   */
  implemented: boolean;
}

/** Per-chain trading-vertical overrides, stored on `ChainConfig`. */
export type TradingTypeOverrides = Partial<Record<TradingTypeKey, boolean>>;

/** Which layer decided a flag's value. Exposed for diagnostics and tests. */
export enum FeatureFlagSource {
  Default = "default",
  Chain = "chain",
  Env = "env",
  DevOverride = "dev-override",
}

export interface FeatureFlagResolution {
  key: FeatureFlagKey;
  enabled: boolean;
  source: FeatureFlagSource;
}
